import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Edit, Trash2, History, Search, X, CalendarIcon, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OperationsHistoryModal } from "../common/OperationsHistoryModal";
import { useUserGroups } from "@/hooks/users/useUserGroups";
import { useDeleteUserGroup } from "@/hooks/users/useDeleteUserGroup";
import { useUserGroupAuditLog } from "@/hooks/users/useUserGroupAuditLog";
import LoaderComponent from "@/components/common/LoaderComponent";
import { formatDate } from "@/utils/dateUtils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const UserGroupsTable = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedGroupForHistory, setSelectedGroupForHistory] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ id: number; name: string } | null>(null);

  const { deleting, deleteUserGroup } = useDeleteUserGroup();
  const { auditLogs, loading: auditLogsLoading } = useUserGroupAuditLog(selectedGroupForHistory?.user_group_id || null);

  const {
    userGroups,
    loading,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    setCurrentPage,
    setEntriesPerPage,
    setFilterType,
    setSearchValue,
    handleSearch,
    clearSearch,
  } = useUserGroups();

  // Client-side pagination
  const paginatedGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    return userGroups.slice(startIndex, endIndex);
  }, [userGroups, currentPage, entriesPerPage]);

  const handleEdit = (id: number) => {
    const qs = searchParams.toString();
    navigate(`/admin/users/groups/edit/${id}${qs ? `?${qs}` : ''}`, {
      state: { returnParams: qs, activeTab: 'user-group' }
    });
  };

  const handleHistory = (group: any) => {
    setSelectedGroupForHistory(group);
    setIsHistoryModalOpen(true);
  };

  const handleDeleteClick = (id: number, name: string) => {
    setGroupToDelete({ id, name });
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;

    const success = await deleteUserGroup(groupToDelete.id);
    if (success) {
      setIsDeleteDialogOpen(false);
      setGroupToDelete(null);
      // Refresh the data by triggering a search
      handleSearch();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const startIndex = (currentPage - 1) * entriesPerPage + 1;
  const endIndex = Math.min(currentPage * entriesPerPage, totalCount);

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => setCurrentPage(i)}
            isActive={currentPage === i}
            className={currentPage === i ? "bg-purple-600 text-white hover:bg-purple-700" : "hover:bg-purple-100"}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return pages;
  };

  if (loading) {
    return <LoaderComponent />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 bg-background">
              <SelectValue placeholder="Select filter" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="user_group_name">User Group Name</SelectItem>
              <SelectItem value="user_count">Total Users</SelectItem>
              <SelectItem value="created_datetime">Created Date</SelectItem>
              <SelectItem value="updated_datetime">Updated Date</SelectItem>
            </SelectContent>
          </Select>
          
          {filterType === 'created_datetime' || filterType === 'updated_datetime' ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-48 justify-start text-left font-normal",
                    !searchValue && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {searchValue ? format(new Date(searchValue), "dd/MM/yyyy") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={searchValue ? new Date(searchValue) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      setSearchValue(format(date, "yyyy-MM-dd"));
                    }
                  }}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Input 
              className="w-48" 
              placeholder={
                filterType === 'user_count'
                  ? "Enter number"
                  : "Enter value"
              }
              type={filterType === 'user_count' ? "number" : "text"}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          )}
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white px-8"
            onClick={handleSearch}
            disabled={loading}
          >
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
          {searchValue && (
            <Button 
              variant="outline"
              onClick={clearSearch}
              disabled={loading}
            >
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show</span>
          <Select 
            value={entriesPerPage.toString()} 
            onValueChange={(value) => setEntriesPerPage(Number(value))}
          >
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">Entries</span>
        </div>
      </div>

      <div id="table-admin-user-groups-container" className="rounded-md border">
        <Table id="table-admin-user-groups">
          <TableHeader>
            <TableRow id="table-admin-user-groups-header-row">
              <TableHead id="header-cell-group-name">User Group Name</TableHead>
              <TableHead id="header-cell-total-users">Total Users</TableHead>
              <TableHead id="header-cell-created-date">Created Date</TableHead>
              <TableHead id="header-cell-updated-date">Updated Date</TableHead>
              <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id="table-admin-user-groups-body">
            {paginatedGroups.length > 0 ? (
              paginatedGroups.map((group) => (
                <TableRow key={group.user_group_id} id={`row-user-group-${group.user_group_id}`}>
                  <TableCell id={`cell-user-group-${group.user_group_id}-name`} className="font-medium">{group.user_group_name}</TableCell>
                  <TableCell id={`cell-user-group-${group.user_group_id}-count`}>{group.user_count}</TableCell>
                  <TableCell id={`cell-user-group-${group.user_group_id}-created`}>{new Date(group.created_datetime).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell id={`cell-user-group-${group.user_group_id}-updated`}>{new Date(group.updated_datetime).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell id={`cell-user-group-${group.user_group_id}-actions`} className="text-right">
                    <div className="flex justify-end gap-2">
                      <IconButtonWithTooltip
                        id={`btn-user-group-${group.user_group_id}-history`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleHistory(group)}
                        tooltip="View History"
                        icon={<History className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-user-group-${group.user_group_id}-edit`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(group.user_group_id)}
                        tooltip="Edit"
                        icon={<Edit className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-user-group-${group.user_group_id}-delete`}
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteClick(group.user_group_id, group.user_group_name)}
                        disabled={deleting}
                        tooltip="Delete"
                        icon={<Trash2 className="h-4 w-4" />}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow id="row-user-groups-empty">
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No user groups found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          {totalCount > 0 ? `Showing ${startIndex} to ${endIndex} of ${totalCount} entries` : 'No entries'}
        </div>
        
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            {renderPageNumbers()}
            <PaginationItem>
              <PaginationNext 
                onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>

      <OperationsHistoryModal
        open={isHistoryModalOpen}
        onOpenChange={setIsHistoryModalOpen}
        entityName={selectedGroupForHistory?.user_group_name || ""}
        entityType="User Group"
        auditLogs={auditLogs}
        loading={auditLogsLoading}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{groupToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Prevent Radix AlertDialog from auto-closing so the loader is visible
                e.preventDefault();
                void handleConfirmDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};